import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Tabs, Tag, Typography, Spin } from 'antd';
import { HistoryOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const LAST_READ_KEY = 'idea_calc_last_read_version';
const CHANGELOG_READ_EVENT = 'changelog-read';

type ChangelogCategory = 'Added' | 'Fixed' | 'Changed' | 'Removed';

interface ChangelogEntry {
  category: ChangelogCategory;
  bugId?: string;
  description: string;
}

interface ChangelogVersion {
  version: string;
  date: string;
  sections: {
    title: string;
    entries: ChangelogEntry[];
  }[];
}

interface ChangelogData {
  currentVersion: string;
  versions: ChangelogVersion[];
}

interface GeneratedChangelogData {
  currentVersion: string;
  versions: Array<{
    version: string;
    released_at: string | null;
    entries: Array<{ type: string; message: string }>;
  }>;
}

const generatedCategory: Record<string, ChangelogCategory> = {
  added: 'Added', fixed: 'Fixed', changed: 'Changed', removed: 'Removed', docs: 'Changed',
};

function normalizeChangelog(data: ChangelogData | GeneratedChangelogData): ChangelogData {
  const first = data.versions[0];
  if (!first || 'sections' in first) return data as ChangelogData;
  return {
    currentVersion: data.currentVersion,
    versions: (data as GeneratedChangelogData).versions.map((version) => ({
      version: version.version,
      date: version.released_at || '',
      sections: [{
        title: version.version,
        entries: version.entries.map((entry) => ({
          category: generatedCategory[entry.type] || 'Changed',
          description: entry.message,
        })),
      }],
    })),
  };
}

// Changelog is loaded dynamically from /changelog.json
let cachedChangelog: ChangelogData | null = null;

// Section IDs are stable identifiers; titles come from i18n.
const GUIDE_SECTION_IDS = [
  'intro',
  'start',
  'calculator',
  'budget',
  'competitors',
  'forecast',
  'export',
  'faq',
] as const;
type GuideSectionId = typeof GUIDE_SECTION_IDS[number];

const categoryColors: Record<ChangelogCategory, string> = {
  Added: 'green',
  Fixed: 'blue',
  Changed: 'orange',
  Removed: 'red',
};

// Map changelog payload category → i18n key (kebab for keys, PascalCase in JSON).
const categoryI18nKeys: Record<ChangelogCategory, string> = {
  Added: 'idea_calc.documentation.category_added',
  Fixed: 'idea_calc.documentation.category_fixed',
  Changed: 'idea_calc.documentation.category_changed',
  Removed: 'idea_calc.documentation.category_removed',
};

interface DocumentationModalProps {
  open: boolean;
  onClose: () => void;
}

const DocumentationModal: React.FC<DocumentationModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('changelog');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [activeGuideSection, setActiveGuideSection] = useState<GuideSectionId>('intro');
  const [changelog, setChangelog] = useState<ChangelogData | null>(cachedChangelog);
  const [loading, setLoading] = useState(!cachedChangelog);

  // Load changelog from JSON file
  useEffect(() => {
    if (cachedChangelog) {
      setChangelog(cachedChangelog);
      setSelectedVersion(cachedChangelog.versions[0]?.version || '');
      return;
    }

    const basePath = import.meta.env.BASE_URL || '/';
    fetch(`${basePath}changelog.json`)
      .then((res) => res.json())
      .then((raw: ChangelogData | GeneratedChangelogData) => {
        const data = normalizeChangelog(raw);
        cachedChangelog = data;
        setChangelog(data);
        setSelectedVersion(data.versions[0]?.version || '');
      })
      .catch((err) => console.error('Failed to load changelog:', err))
      .finally(() => setLoading(false));
  }, []);

  // Mark as read when modal closes (user has seen the changelog)
  useEffect(() => {
    if (!open && changelog) {
      const lastRead = localStorage.getItem(LAST_READ_KEY);
      if (lastRead !== changelog.currentVersion) {
        localStorage.setItem(LAST_READ_KEY, changelog.currentVersion);
        window.dispatchEvent(new Event(CHANGELOG_READ_EVENT));
      }
    }
  }, [open, changelog]);

  const selectedChangelogVersion = useMemo(
    () => changelog?.versions.find((v) => v.version === selectedVersion),
    [changelog, selectedVersion]
  );

  const currentVersion = changelog?.currentVersion || t('idea_calc.documentation.version_loading');
  const guideHtml = t(`idea_calc.documentation.guide_content.${activeGuideSection}`);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      title={t('idea_calc.documentation.title', { version: currentVersion })}
      styles={{
        body: { padding: 0, minHeight: 500 },
      }}
      style={{ top: 40 }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ padding: '0 16px' }}
        items={[
          {
            key: 'changelog',
            label: (
              <span>
                <HistoryOutlined /> {t('idea_calc.documentation.tab_changelog')}
              </span>
            ),
            children: loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 450 }}>
                <Spin />
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 16, minHeight: 450 }}>
                {/* Version list */}
                <div style={{ width: 160, borderRight: '1px solid #f0f0f0', paddingRight: 16 }}>
                  {changelog?.versions.map((v) => (
                    <div
                      key={v.version}
                      onClick={() => setSelectedVersion(v.version)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: 6,
                        marginBottom: 4,
                        background: selectedVersion === v.version ? '#e6f4ff' : 'transparent',
                        borderLeft: selectedVersion === v.version ? '3px solid #1890ff' : '3px solid transparent',
                        transition: 'all 0.2s',
                      }}
                    >
                      <Typography.Text strong={selectedVersion === v.version}>
                        {v.version}
                      </Typography.Text>
                      <br />
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {t('idea_calc.documentation.version_date_prefix')} {v.date}
                      </Typography.Text>
                    </div>
                  ))}
                </div>

                {/* Changelog content */}
                <div style={{ flex: 1, overflow: 'auto', maxHeight: 450, paddingRight: 8 }}>
                  {selectedChangelogVersion?.sections.map((section, idx) => (
                    <div key={idx} style={{ marginBottom: 16 }}>
                      <Typography.Text strong style={{ fontSize: 13 }}>
                        {section.title}
                      </Typography.Text>
                      <div style={{ marginTop: 8 }}>
                        {section.entries.map((entry, entryIdx) => (
                          <div
                            key={entryIdx}
                            style={{
                              display: 'flex',
                              alignItems: 'baseline',
                              marginBottom: 6,
                              paddingLeft: 12,
                            }}
                          >
                            <div style={{ width: 80, flexShrink: 0 }}>
                              <Tag color={categoryColors[entry.category]} style={{ margin: 0, fontSize: 11 }}>
                                {t(categoryI18nKeys[entry.category])}
                              </Tag>
                            </div>
                            <div style={{ width: 60, flexShrink: 0 }}>
                              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                {entry.bugId || ''}
                              </Typography.Text>
                            </div>
                            <Typography.Text style={{ fontSize: 13, flex: 1 }}>{entry.description}</Typography.Text>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ),
          },
          {
            key: 'guide',
            label: (
              <span>
                <QuestionCircleOutlined /> {t('idea_calc.documentation.tab_guide')}
              </span>
            ),
            children: (
              <div style={{ display: 'flex', gap: 16, minHeight: 450 }}>
                {/* Table of contents */}
                <div style={{ width: 200, borderRight: '1px solid #f0f0f0', paddingRight: 16 }}>
                  {GUIDE_SECTION_IDS.map((id) => (
                    <div
                      key={id}
                      onClick={() => setActiveGuideSection(id)}
                      style={{
                        padding: '6px 12px',
                        cursor: 'pointer',
                        borderRadius: 4,
                        marginBottom: 2,
                        background: activeGuideSection === id ? '#e6f4ff' : 'transparent',
                        fontSize: 13,
                      }}
                    >
                      <Typography.Text strong={activeGuideSection === id}>
                        {t(`idea_calc.documentation.guide_sections.${id}`)}
                      </Typography.Text>
                    </div>
                  ))}
                </div>

                {/* Guide content — rendered from localized HTML.
                    Source comes from our own i18n bundles (no user input),
                    so dangerouslySetInnerHTML is safe here. */}
                <div
                  className="documentation-guide-content"
                  style={{ flex: 1, overflow: 'auto', maxHeight: 450, paddingRight: 8 }}
                  dangerouslySetInnerHTML={{ __html: guideHtml }}
                />
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
};

// Hook to check if there are unread updates
export function useHasUnreadUpdates(): boolean {
  const [hasUnread, setHasUnread] = useState(false);

  const checkUnread = () => {
    const lastRead = localStorage.getItem(LAST_READ_KEY);
    if (cachedChangelog) {
      setHasUnread(lastRead !== cachedChangelog.currentVersion);
    }
  };

  useEffect(() => {
    // Listen for changelog read events
    window.addEventListener(CHANGELOG_READ_EVENT, checkUnread);

    // If we have cached changelog, use it
    if (cachedChangelog) {
      checkUnread();
      return () => window.removeEventListener(CHANGELOG_READ_EVENT, checkUnread);
    }

    // Otherwise fetch changelog
    const basePath = import.meta.env.BASE_URL || '/';
    fetch(`${basePath}changelog.json`)
      .then((res) => res.json())
      .then((data: ChangelogData) => {
        cachedChangelog = data;
        checkUnread();
      })
      .catch(() => setHasUnread(false));

    return () => window.removeEventListener(CHANGELOG_READ_EVENT, checkUnread);
  }, []);

  return hasUnread;
}

export default DocumentationModal;
